import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  applyResolvedTheme,
  effectiveMode,
  getAccountMirror,
  getStoredMode,
  resolveTheme,
  setAccountMirror,
  setStoredMode,
  type ThemeMode,
} from './theme';

/** Account persistence, injected per app so this package stays supabase-free
 *  (same pattern as DevPanelDeps.pollSyncJob). Omit either to disable. */
export interface ThemeDeps {
  /** Returns user_settings.theme_preference, or null if unauthenticated/unset. */
  loadAccountTheme?: () => Promise<ThemeMode | null>;
  saveAccountTheme?: (mode: ThemeMode) => Promise<void>;
}

interface ThemeContextValue {
  /** The mode in effect on this device ('system' until the user picks). */
  mode: ThemeMode;
  resolvedTheme: 'light' | 'dark';
  /** Apply + persist on this device; optionally also save to the account. */
  setMode(mode: ThemeMode, opts?: { saveToAccount?: boolean }): Promise<void>;
  canSaveToAccount: boolean;
  /** Re-fetch the account theme (call after sign-in — the mount-time sync ran
   *  before a session existed, so the login page couldn't see it). */
  refreshAccountTheme(): Promise<void>;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ deps, children }: { deps?: ThemeDeps; children: ReactNode }) {
  // Initializer mirrors THEME_INIT_SCRIPT exactly — no first-render flash.
  const [mode, setModeState] = useState<ThemeMode>(() => effectiveMode());
  const [resolvedTheme, setResolved] = useState<'light' | 'dark'>(() => resolveTheme(effectiveMode()));

  // Apply on mode change; while in 'system', follow the OS live.
  useEffect(() => {
    const apply = () => {
      const t = resolveTheme(mode);
      setResolved(t);
      applyResolvedTheme(t);
    };
    apply();
    if (mode !== 'system') return;
    const mq = matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [mode]);

  // Account sync: refresh the mirror; adopt the account value only when this
  // device has no explicit choice (account stays the driver here until the
  // user picks locally — we do NOT write the device key). Runs at mount and
  // again via refreshAccountTheme() right after sign-in.
  const refreshAccountTheme = useCallback(async () => {
    if (!deps?.loadAccountTheme) return;
    try {
      const acct = await deps.loadAccountTheme();
      setAccountMirror(acct);
      if (acct && getStoredMode() === null) setModeState(acct);
    } catch {
      /* account theme is best-effort; device/system already applied */
    }
  }, [deps]);

  useEffect(() => {
    void refreshAccountTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setMode = useCallback(
    async (m: ThemeMode, opts?: { saveToAccount?: boolean }) => {
      setStoredMode(m);
      setModeState(m);
      if (opts?.saveToAccount) {
        if (!deps?.saveAccountTheme) throw new Error('Account persistence unavailable');
        await deps.saveAccountTheme(m);
        setAccountMirror(m);
      }
    },
    [deps]
  );

  const value = useMemo(
    () => ({
      mode,
      resolvedTheme,
      setMode,
      canSaveToAccount: !!deps?.saveAccountTheme,
      refreshAccountTheme,
    }),
    [mode, resolvedTheme, setMode, deps, refreshAccountTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === undefined) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}

// Re-export the account-mirror getter for app boot logic that needs it.
export { getAccountMirror };
